import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { HalfPageLayout } from 'bxcommon/components/HalfPageLayout/HalfPageLayout';
import { BaseLoaderSpinner, ThankYouPage } from 'bxcommon';
import { useCheckPaymentStatusQuery } from 'bxcommon/store/endpoints/resources.endpoints';
import { PAYMENT_STATUSES } from 'bxcommon/constants/payments.constant';
import { successToast, warningToast } from 'bxcommon/store/slices/toaster.slice';
import errorService from 'bxcommon/helpers/errors.helper';

import { AdvisorHeader } from '../AdvisorHeader/AdvisorHeader';
import './ThankYou.scss';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60_000;

const TERMINAL_STATUSES = [PAYMENT_STATUSES.SUCCESS, PAYMENT_STATUSES.TRUSTED, PAYMENT_STATUSES.FAILURE];

export const ThankYouAdvisor = () => {
  const { t } = useTranslation();
  const { t: common } = useTranslation('common');

  const navigate = useNavigate();
  const { caseId } = useParams();

  const [loading, setLoading] = useState(Boolean(caseId));
  const [pollInterval, setPollInterval] = useState(POLL_INTERVAL_MS);
  const [startedAt] = useState(() => Date.now());

  const { data: response, error, isError } = useCheckPaymentStatusQuery(caseId, {
    skip: !caseId,
    pollingInterval: pollInterval,
  });

  useEffect(() => {
    if (!caseId || !response) return;

    const status = response.status;
    const terminal = TERMINAL_STATUSES.includes(status);
    const timedOut = Date.now() - startedAt > POLL_TIMEOUT_MS;

    if (!terminal && !timedOut) return;

    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setPollInterval(0);
    setLoading(false);

    if (status === PAYMENT_STATUSES.SUCCESS || status === PAYMENT_STATUSES.TRUSTED) {
      successToast(common('request.toasts.requestSubmittedMessage'), common('request.toasts.requestSubmittedTitle'));
    } else {
      warningToast(common('messages.failedPayment'));
      navigate('/request-form');
    }
  }, [caseId, response, startedAt, common, navigate]);

  useEffect(() => {
    if (isError && error) {
      console.error(error);
      errorService.showBackendErrors(error);
      // oxlint-disable-next-line react-hooks/set-state-in-effect
      setPollInterval(0);
      setLoading(false);
    }
  }, [isError, error]);

  const loadingContent = (
    <div className="thank-you__loading thank-you--advisor">
      <BaseLoaderSpinner isComponent={true} large={true} />
      <p className="thank-you__loading-description">{t('request.thankYou.paymentPending')}</p>
    </div>
  );

  return (
    <AdvisorHeader>
      {loading && caseId ? (
        <HalfPageLayout rightContent={loadingContent} />
      ) : (
        <ThankYouPage
          isAdvisor
          link={caseId ? `/request-details/${caseId}` : '/request-details'}
          description={t('request.thankYou.description')}
        />
      )}
    </AdvisorHeader>
  );
};

export default ThankYouAdvisor;
